/**
 * A design is many sheets, and they join by name.
 *
 * CIRCAD's help for Place Signal Port says what a port is for: a signal
 * "connected to other sheets or other places on the same sheet". Both halves
 * are the same mechanism — a port is a component whose pad carries a
 * `Signal:`, and every pad of that name is one net — so a design's netlist is
 * each sheet scanned on its own, then the named nets collected across all of
 * them.
 *
 * **Geometry never crosses a sheet.** This is the whole reason this module
 * exists rather than the sheets being concatenated into one document and handed
 * to {@link extractNetlist}. Every sheet is drawn in its own frame, around the
 * same origin, so two sheets laid on top of each other have pins and wires at
 * identical coordinates — and the scan joins primitives that touch. Merging the
 * entities merged the drawings: on two sheets each holding one resistor and one
 * wire, sharing nothing at all, the netlist came back with two nets, each
 * shorting a pin on sheet 1 to a pin on sheet 2. Net List In then wrote those
 * invented names onto the board's pads.
 *
 * So each sheet is scanned alone and only the *names* are collected. That is
 * both correct and the original's own model: a net is every primitive carrying
 * that signal name, wherever it is drawn.
 */

import type { CadDocument } from '../model.ts';
import {
  extractNetlist, type Net, type Netlist, type NetConflict, type PinRef,
} from './netlist.ts';

/** A sheet of a design, and where it sits in the set. */
export interface Sheet {
  doc: CadDocument;
  /** Position in the design, from zero. */
  index: number;
  name: string;
}

/** Wrap a list of documents as sheets, in the order they are given. */
export function sheetsOf(docs: readonly CadDocument[]): Sheet[] {
  return docs.map((doc, index) => ({ doc, index, name: doc.name || `Sheet ${index + 1}` }));
}

/** A design's netlist, with what it took from each sheet. */
export interface DesignNetlist extends Netlist {
  /** How many sheets were read. */
  sheets: number;
  /**
   * Nets that appear on more than one sheet — the ports doing their job.
   *
   * Worth reporting on its own: a design whose sheets share no net at all is
   * either genuinely independent or, far more often, missing the ports that
   * were supposed to join them, and the netlist cannot tell the difference
   * without saying what it found.
   */
  crossSheet: string[];
}

/**
 * The netlist of a whole design.
 *
 * One sheet is the same as calling {@link extractNetlist} on it, except for the
 * sheet-tagging on each pin.
 */
export function extractDesignNetlist(docs: readonly CadDocument[]): DesignNetlist {
  const sheets = sheetsOf(docs);

  if (sheets.length === 0) {
    return {
      nets: [], conflicts: [], unconnected: [], noConnect: [],
      sheets: 0, crossSheet: [],
    };
  }

  const conflicts: NetConflict[] = [];
  const unconnected: PinRef[] = [];
  const noConnect: PinRef[] = [];

  /** Named nets, gathered across sheets, and which sheets each was seen on. */
  const named = new Map<string, Net>();
  const onSheets = new Map<string, Set<number>>();
  /** Unnamed nets: one island of wire nobody claimed, and never merged. */
  const loose: Net[] = [];
  let generated = 0;

  for (const sheet of sheets) {
    const one = extractNetlist(sheet.doc);
    const tag = (p: PinRef): PinRef => ({ ...p, sheet: sheet.index, sheetName: sheet.name });

    conflicts.push(...one.conflicts);
    unconnected.push(...one.unconnected.map(tag));
    noConnect.push(...one.noConnect.map(tag));

    for (const net of one.nets) {
      const pins = net.pins.map(tag);
      const groups = net.groups.map((g) => g.map(tag));

      if (!net.named) {
        // Each sheet generates `N$001` upwards, so the names collide across a
        // design and would read as one net in every report. Renumbered here,
        // in sheet order, which is also the order they are met.
        loose.push({
          ...net,
          name: `N$${String(++generated).padStart(3, '0')}`,
          pins,
          groups,
        });
        continue;
      }

      const existing = named.get(net.name);
      if (!existing) {
        named.set(net.name, { ...net, pins, groups });
        onSheets.set(net.name, new Set([sheet.index]));
        continue;
      }
      // Same name on two sheets is one net. That is what a signal port is for,
      // and the geometry stays apart: each sheet's island is its own group, so
      // a board still gets a ratline for what is not yet routed.
      existing.pins.push(...pins);
      existing.entityIds.push(...net.entityIds);
      existing.groups.push(...groups);
      onSheets.get(net.name)!.add(sheet.index);
    }
  }

  const crossSheet: string[] = [];
  const nets: Net[] = [];
  for (const [name, net] of named) {
    const on = onSheets.get(name)!;
    if (on.size > 1) crossSheet.push(name);
    nets.push({ ...net, sheets: [...on].sort((a, b) => a - b) });
  }
  nets.push(...loose);
  nets.sort((a, b) => a.name.localeCompare(b.name, undefined, { numeric: true }));

  crossSheet.sort((a, b) => a.localeCompare(b, undefined, { numeric: true }));

  return {
    nets, conflicts, unconnected, noConnect,
    sheets: sheets.length,
    crossSheet,
  };
}
